import { useEffect, useState } from "react";
import Link from "next/link";
import { useTranslation } from "react-i18next";

import { getCategoryMenu } from "@/services/courseService";
import { ROUTE } from "@/route/app_routes.js";

const ICONS = {
  "backend development": "feather-server",
  "frontend development": "feather-monitor",
  "full-stack development": "feather-layers",
  "data analyses": "feather-bar-chart-2",
  "data analysis": "feather-bar-chart-2",
  "mobile app development": "feather-smartphone",
  "mobile development": "feather-smartphone",
  "game development": "feather-play",
  "3d modeling": "feather-box",
  "graphics design": "feather-edit-3",
  "ui/ux design": "feather-layout",
  "product design": "feather-layout",
  devops: "feather-cloud",
  "cyber security": "feather-shield",
};

const getIcon = (name) => ICONS[(name || "").toLowerCase()] || "feather-code";

// Rotated per card index -- four on-brand gradient variants (all built from --color-primary /
// --color-secondary) so the grid reads as varied and alive rather than one repeated purple tile,
// without breaking the palette the rest of the site is built on.
const ACCENTS = ["rbt-cat-card--a", "rbt-cat-card--b", "rbt-cat-card--c", "rbt-cat-card--d"];

const SKELETON_COUNT = 6;

const Category = () => {
  const { t } = useTranslation("home");
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getCategoryMenu(3)
      .then((res) => setCategories(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="rbt-cat-showcase">
        {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
          <div className="rbt-cat-card" key={i}>
            <div className="rbt-skel" style={{ width: 52, height: 52, borderRadius: 14, marginBottom: 20 }} />
            <div className="rbt-skel rbt-course-card__skel-line rbt-course-card__skel-line--w60" style={{ height: 18 }} />
            <div className="rbt-skel rbt-course-card__skel-line rbt-course-card__skel-line--w90" />
          </div>
        ))}
      </div>
    );
  }

  if (categories.length === 0) return null;

  // Cap the homepage teaser to one clean grid -- the section already links to a "View All"
  // page for the full list, so this never has to worry about an orphaned partial row as the
  // real category count grows over time.
  const featured = categories.slice(0, 6);

  return (
    <div className="rbt-cat-showcase">
      {featured.map((cat, index) => (
        <Link
          className={`rbt-cat-card ${ACCENTS[index % ACCENTS.length]}`}
          href={`${ROUTE.course}?category=${encodeURIComponent(cat.name)}`}
          key={cat.slug}
        >
          <span className="rbt-cat-card__glow" aria-hidden="true" />
          <i className={`rbt-cat-card__watermark ${getIcon(cat.name)}`} aria-hidden="true"></i>
          <span className="rbt-cat-card__icon">
            <i className={getIcon(cat.name)}></i>
          </span>
          <h3 className="rbt-cat-card__title">{cat.name}</h3>
          <p className="rbt-cat-card__count">
            {cat.course_count} {cat.course_count !== 1 ? t("categories.coursesLabel", "courses") : t("categories.courseLabel", "course")}
          </p>

          {cat.courses?.length > 0 && (
            <ul className="rbt-cat-card__samples">
              {cat.courses.slice(0, 3).map((c) => (
                <li key={c.id}>
                  <i className="feather-arrow-up-right"></i>
                  {c.title}
                </li>
              ))}
            </ul>
          )}

          <span className="rbt-cat-card__link">
            {t("categories.exploreTrack", "Explore track")} <i className="feather-arrow-right"></i>
          </span>
        </Link>
      ))}
    </div>
  );
};

export default Category;
